import { useCallback, useEffect, useState } from 'react'
import { suggest } from '@/lib/api'
import {
  getZoomEnvironment,
  onParticipantChange,
  type ZoomEnvironment,
} from '@/lib/zoom-sdk'
import type {
  AssistantChatMessage,
  ChatMessage,
  SuggestResponse,
  SuggestionsPayload,
  TemplateName,
  UserChatMessage,
} from '@/lib/types'
import { ChatLog } from '@/components/ChatLog'
import { ChatInput } from '@/components/ChatInput'
import { MeetingContextBar } from '@/components/MeetingContextBar'
import { StandaloneBanner } from '@/components/StandaloneBanner'
import { TemplatePicker } from '@/components/TemplatePicker'

export function App() {
  const [env, setEnv] = useState<ZoomEnvironment | null>(null)
  const [template, setTemplate] = useState<TemplateName>('sales-discovery')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')

  // Number of in-flight AI requests. Derived from messages — every assistant
  // bubble that's still loading counts as one. Used purely to render the
  // "N thinking…" indicator next to the chat input; never blocks sending.
  const pendingCount = messages.filter(
    (m) => m.kind === 'assistant' && m.isLoading,
  ).length

  // Boot the Zoom SDK once.
  useEffect(() => {
    let cancelled = false
    getZoomEnvironment().then((next) => {
      if (!cancelled) setEnv(next)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Live participant updates inside Zoom — fires when someone joins/leaves
  // mid-call. No-op outside Zoom (the function exits early in standalone).
  useEffect(() => {
    if (!env?.inZoom) return
    const unsubscribe = onParticipantChange((participants) => {
      setEnv((prev) =>
        prev
          ? {
              ...prev,
              meetingContext: { ...prev.meetingContext, participants },
            }
          : prev,
      )
    })
    return unsubscribe
  }, [env?.inZoom])

  /**
   * Send the current input as a new user message and trigger a fresh
   * suggestion based on the cumulative notes from ALL prior user messages
   * plus this one. The Worker is stateless; we re-send the full notes
   * blob each turn so the AI reasons over the totality.
   */
  const handleSend = useCallback(async () => {
    const text = input.trim()
    if (!text) return

    const now = new Date().toISOString()
    const userMsg: UserChatMessage = {
      kind: 'user',
      id: `u-${crypto.randomUUID()}`,
      ts: now,
      content: text,
    }
    const placeholder: AssistantChatMessage = {
      kind: 'assistant',
      id: `a-${crypto.randomUUID()}`,
      ts: now,
      suggestions: null,
      meta: null,
      isLoading: true,
      error: null,
      template,
    }

    // Compose cumulative notes from every prior user message + this one. Captured
    // in this closure at send-time, so even if more messages fire before this
    // one resolves, the AI still sees the correct point-in-time context.
    const cumulativeNotes = [
      ...messages.filter((m): m is UserChatMessage => m.kind === 'user'),
      userMsg,
    ]
      .map((m) => m.content)
      .join('\n\n')

    // Snapshot the active template too — if the user changes it mid-flight,
    // this request still uses the template they had selected at send-time.
    const requestTemplate = template
    const requestMeetingContext = env?.meetingContext

    setMessages((prev) => [...prev, userMsg, placeholder])
    setInput('')

    // Fire-and-forget: don't await. Multiple in-flight requests are fine —
    // each updates only its own placeholder by id.
    void (async () => {
      try {
        const result = await suggest({
          template: requestTemplate,
          notes: cumulativeNotes,
          meetingContext: requestMeetingContext,
        })
        setMessages((prev) =>
          prev.map((m) =>
            m.id === placeholder.id
              ? {
                  ...placeholder,
                  suggestions: stripMeta(result),
                  meta: result._meta,
                  isLoading: false,
                }
              : m,
          ),
        )
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        setMessages((prev) =>
          prev.map((m) =>
            m.id === placeholder.id
              ? { ...placeholder, isLoading: false, error: message }
              : m,
          ),
        )
        console.error('[suggest] failed', err)
      }
    })()
  }, [env?.meetingContext, input, messages, template])

  /**
   * Pre-fill the chat input with markdown from a suggestion section.
   * In chat mode, this becomes the rep's next message — they can edit
   * before sending or send as-is to refine their accumulated notes.
   */
  const insertToInput = useCallback((markdown: string) => {
    setInput((prev) => {
      const sep = prev.length === 0 ? '' : prev.endsWith('\n') ? '' : '\n\n'
      return prev + sep + markdown
    })
  }, [])

  // Boot loader while the SDK config() runs.
  if (!env) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-zinc-500">
        Loading…
      </div>
    )
  }

  // Show the meeting-context bar only when there's actually meeting info to show.
  // In standalone mode the banner already explains the absence; in Zoom mode
  // with empty SDK capabilities we have no title to show, so hide it too.
  const hasMeetingInfo =
    Boolean(env.meetingContext.title) ||
    Boolean(env.meetingContext.participants?.length)

  return (
    <div className="flex h-full flex-col bg-zinc-50 text-zinc-900">
      {!env.inZoom && <StandaloneBanner />}
      {env.inZoom && hasMeetingInfo && (
        <MeetingContextBar meeting={env.meetingContext} inZoom={env.inZoom} />
      )}
      <TemplatePicker value={template} onChange={setTemplate} />

      {/* Chat log fills available space and scrolls. */}
      <div className="flex-1 overflow-y-auto">
        <ChatLog messages={messages} onInsertToInput={insertToInput} />
      </div>

      <ChatInput
        value={input}
        onChange={setInput}
        onSend={handleSend}
        pendingCount={pendingCount}
        autoFocus
      />
    </div>
  )
}

/** Drop `template` + `_meta` from a SuggestResponse to leave a SuggestionsPayload. */
function stripMeta(r: SuggestResponse): SuggestionsPayload {
  const { summary, ideas } = r
  return { summary, ideas }
}
